import { Component, Element, Event, EventEmitter, h, Host, Prop, Watch } from '@stencil/core'

import Helpers from '../../../utils/helpers'
import { CssClassMap } from '../../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/tab?tab=designer).
 *
 * @slot default - Slot para o conteúdo do item da aba.
 */
@Component({
  tag: 'br-tab-item',
  styleUrl: 'tab-item.scss',
  shadow: true,
})
export class TabItem {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrTabItemElement

  /**
   * Identificador único do item da tab.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true, mutable: true }) tabItemId: string = Helpers.generateUniqueId()

  /**
   * Define o título do item de tab que aparece no botão de navegação
   */
  @Prop({ reflect: true }) readonly tabItemTitle: string

  /**
   * Define ícone do item de tab
   */
  @Prop({ reflect: true }) readonly icon: string

  /**
   * Define se exibe apenas o ícone, sem texto
   */
  @Prop({ reflect: true }) readonly onlyIcon: boolean = false

  /**
   * Define se o item de tab está ativo/visível
   */

  @Prop({ reflect: true, mutable: true }) isActive: boolean = false

  /**
   * Desativa o item de tab, impedindo que seja selecionado pelo usuário.
   * O conteúdo do painel correspondente permanece inacessível enquanto desativado.
   */
  @Prop({ reflect: true }) readonly disabled: boolean = false

  /**
   * Define contador do item de tab (exibido abaixo do botão)
   */
  @Prop({ reflect: true }) readonly counter: string

  /**
   * Define se o Tab usará um esquema de cores escuro
   */
  @Prop({ reflect: true }) readonly colorMode: 'dark'

  /**
   * Evento disparado quando as propriedades do tab-item mudam.
   * O componente pai (br-tab) escuta este evento para atualizar a navegação.
   */
  @Event() brTabItemPropsChange: EventEmitter<void>

  /**
   * Observa mudanças no título e notifica o pai
   */
  @Watch('tabItemTitle')
  tabItemTitleChanged() {
    this.brTabItemPropsChange.emit()
  }

  /**
   * Observa mudanças no ícone e notifica o pai
   */
  @Watch('icon')
  iconChanged() {
    this.brTabItemPropsChange.emit()
  }

  /**
   * Observa mudanças no contador e notifica o pai
   */
  @Watch('counter')
  counterChanged() {
    this.brTabItemPropsChange.emit()
  }

  /**
   * Observa mudanças em onlyIcon e notifica o pai
   */
  @Watch('onlyIcon')
  onlyIconChanged() {
    this.brTabItemPropsChange.emit()
  }

  /**
   * Observa mudanças em disabled e notifica o pai
   */
  @Watch('disabled')
  disabledChanged() {
    this.brTabItemPropsChange.emit()
  }

  componentWillLoad() {
    // Garante ID único
    if (
      this.tabItemId === undefined ||
      this.tabItemId === null ||
      (typeof this.tabItemId === 'string' && this.tabItemId === '')
    ) {
      this.tabItemId = Helpers.generateUniqueId()
    }

    // Suporte para o atributo 'active' (além de 'is-active')
    if (this.el.hasAttribute('active')) {
      this.isActive = true
    }
  }

  private getCssClassMap(): CssClassMap {
    return {
      'tab-panel': true,
      active: this.isActive,
      disabled: this.disabled,
      'dark-mode': this.colorMode === 'dark',
    }
  }

  render() {
    return (
      <Host
        class={this.getCssClassMap()}
        id={`tab-panel-${this.tabItemId}`}
        role="tabpanel"
        aria-labelledby={`tab-button-${this.tabItemId}`}
        aria-hidden={this.isActive && !this.disabled ? 'false' : 'true'}
      >
        <slot></slot>
      </Host>
    )
  }
}
